import NextImage from 'next/image'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { Input } from '@wartek-id/input'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import {
  Card,
  CardBody,
  CardHeader,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { SelectSupportNeed } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SelectSupportNeed'

import type { HeadingLevel } from 'types/typography'
import type { IFollowUpDocumentForm } from 'types/kinerja-pelaksanaan'

export type FormPilihanBelajarLainnyaProps = {
  headingLevel?: HeadingLevel
  hideIfOptional?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
}
export function FormPilihanBelajarLainnya({
  headingLevel = 'h2',
  hideIfOptional = false,
  isDisabled = false,
  isReadOnly = false,
}: FormPilihanBelajarLainnyaProps) {
  const formContext = useFormContext<IFollowUpDocumentForm>()

  if (!formContext) {
    throw new Error(
      'FormPilihanBelajarLainnya must be used within `<FormProvider />`'
    )
  }

  const { register, watch, getValues } = formContext

  const values = watch([
    'additionalFollowUpTarget',
    'additionalFollowUpEffort',
    'additionalFollowUpPeriod',
    'additionalSupportNeed',
  ])

  const isRequired = useMemo(() => {
    for (const value of values) {
      if (!!value) return true
    }

    return false
  }, [values])

  if (!isRequired && hideIfOptional) {
    return null
  }

  return (
    <Card>
      <CardHeader className="flex space-x-4 py-4 items-center">
        <NextImage
          src="/illustrations/img-assessment-subject-book.png"
          width="64"
          height="64"
        />
        <Text variant="heading-md" className="flex-1" as={headingLevel}>
          Pilihan Belajar
        </Text>
      </CardHeader>

      <CardBody className="p-6 space-y-4">
        <fieldset className="space-y-2">
          <Text variant="heading-sm" id="description-follow-up-target">
            Apa tujuan tindak lanjut yang ingin Anda lakukan untuk meningkatkan
            kualitas Praktik Kinerja Anda?
          </Text>
          <div className="space-y-1">
            <Text
              as="label"
              htmlFor="additionalFollowUpTarget"
              color="subdued"
              aria-hidden={isReadOnly}
            >
              Tujuan Tindak Lanjut
            </Text>
            {isReadOnly ? (
              <>
                <Text className="overflow-wrap-anywhere" aria-hidden="true">
                  {getValues('additionalFollowUpTarget')}
                </Text>
                <span className="sr-only">
                  Tujuan Tindak Lanjut, {getValues('additionalFollowUpTarget')}
                </span>
              </>
            ) : (
              <div className="max-w-full lg:w-4/6">
                <Textarea
                  {...register('additionalFollowUpTarget', {
                    maxLength: 500,
                    required: true,
                  })}
                  isDisabled={isDisabled}
                  id="additionalFollowUpTarget"
                  placeholder="Ketik di sini"
                  maxLength={500}
                  rows={3}
                />
              </div>
            )}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          {!isReadOnly && (
            <Text variant="heading-sm">
              Apa upaya-upaya yang ingin Anda lakukan untuk mencapai tujuan
              tindak lanjut tersebut?
            </Text>
          )}
          <div className="space-y-1">
            <Text
              as="label"
              htmlFor="additionalFollowUpEffort"
              color="subdued"
              aria-hidden={isReadOnly}
            >
              Upaya Tindak Lanjut
            </Text>
            {isReadOnly ? (
              <>
                <Text
                  className="overflow-wrap-anywhere"
                  aria-labelledby="abel-followup-effort"
                >
                  {getValues('additionalFollowUpEffort')}
                </Text>
                <span className="sr-only">
                  Upaya Tindak Lanjut, {getValues('additionalFollowUpEffort')}
                </span>
              </>
            ) : (
              <div className="max-w-full lg:w-4/6">
                <Textarea
                  {...register('additionalFollowUpEffort', {
                    required: true,
                    maxLength: 500,
                  })}
                  className="max-w-full lg:w-4/6"
                  isDisabled={isDisabled}
                  id="additionalFollowUpEffort"
                  placeholder="Ketik di sini"
                  maxLength={500}
                  rows={3}
                />
              </div>
            )}
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <Text variant="heading-sm">
            Kapan Anda akan melakukan aksi tindak lanjut?
          </Text>
          <div className="space-y-1">
            <Text
              as="label"
              htmlFor="additionalFollowUpPeriod"
              color="subdued"
              aria-hidden={isReadOnly}
            >
              Periode Tindak Lanjut
            </Text>
            {isReadOnly ? (
              <>
                <Text className="overflow-wrap-anywhere" aria-hidden="true">
                  {getValues('additionalFollowUpPeriod')}
                </Text>
                <span className="sr-only">
                  Periode Tindak Lanjut, {getValues('additionalFollowUpPeriod')}
                </span>
              </>
            ) : (
              <div className="max-w-full lg:w-4/6">
                <Input
                  {...register('additionalFollowUpPeriod', {
                    required: true,
                    maxLength: 250,
                  })}
                  isDisabled={isDisabled}
                  id="additionalFollowUpPeriod"
                  placeholder="Ketik di sini"
                  maxLength={250}
                />
              </div>
            )}
          </div>
        </fieldset>

        <SelectSupportNeed
          fieldName="additionalSupportNeed"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired
        />
      </CardBody>
    </Card>
  )
}
